import { useQueryClient } from '@tanstack/react-query'
import { ArrowUpFromLine, BadgeCheck, ChevronDown, Coins, FileBarChart, LayoutDashboard, LogOut, Megaphone, Network, PanelLeftClose, PanelLeftOpen, RadioTower, Scale, ScrollText, Settings, Share2, ShieldCheck, Undo2, UserPlus, Users } from 'lucide-react'
import { hasPermission, type Permission } from '@app/shared'
import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { signOut } from '../../api/admin'
import { BrandMark } from './BrandMark'
import { GlobalSearch, type SearchDestination } from './Search'
import styles from './Shell.module.css'
import { HealthIndicator, PlatformClock } from './TopbarStatus'

interface Item {
  to: string
  label: string
  icon: LucideIcon
  /** Shown as a count badge, e.g. withdrawals waiting on a decision. */
  badge?: number
  /** The badge's tone: a queue is a warning, a missing safeguard is a problem. */
  tone?: 'warning' | 'down'
}

interface Group {
  title: string
  items: readonly Item[]
}

/**
 * What each screen needs; the server enforces the same matrix. Security is
 * everyone's. A list means any one of them: people are read by the support
 * desk and by the finance desk, which corrects their ledgers.
 */
const PERMISSION_OF: Record<string, Permission | readonly Permission[] | null> = {
  '/': 'overview',
  '/withdrawals': 'withdrawals',
  '/deposit-reversals': 'ledger',
  '/futures': 'ledger',
  '/kyc': 'kyc',
  '/users': ['users', 'ledger'],
  '/masters': 'masters',
  '/signals': 'signals',
  '/referrals': 'referrals',
  '/invites': 'referrals',
  '/networks': 'registry',
  '/announcements': 'announcements',
  '/reports': 'reports',
  '/settings': 'settings',
  '/audit': 'audit',
  '/security': null,
}

const ROLE_LABEL: Record<string, string> = { admin: 'админ', support: 'поддержка', finance: 'финансы', content: 'контент' }

export function canOpen(role: string | undefined, path: string): boolean {
  const permission = PERMISSION_OF[path]
  if (permission === null) return true
  if (permission === undefined || role === undefined) return false
  return typeof permission === 'string' ? hasPermission(role, permission) : permission.some((one) => hasPermission(role, one))
}

/**
 * `copyEnabled` — the `copy.enabled` setting. Copy trading is off since the
 * 28 September pivot: the masters item leaves the menu (the address still
 * opens the screen, which says the section is switched off).
 */
export function adminNav(pendingWithdrawals = 0, pendingKyc = 0, totpEnabled = true, role: string | undefined = 'admin', pendingDepositReversals = 0, copyEnabled = false): readonly Group[] {
  return allGroups(pendingWithdrawals, pendingKyc, totpEnabled, pendingDepositReversals)
    .map((group) => ({ ...group, items: group.items.filter((item) => canOpen(role, item.to) && (item.to !== '/masters' || copyEnabled)) }))
    .filter((group) => group.items.length > 0)
}

/**
 * Navigation is grouped by what an operator is doing, not by which table the
 * data lives in: approvals first because that is the job with a queue behind it.
 */
function allGroups(pendingWithdrawals: number, pendingKyc: number, totpEnabled: boolean, pendingDepositReversals: number): readonly Group[] {
  return [
    {
      title: 'Работа',
      items: [
        { to: '/', label: 'Обзор', icon: LayoutDashboard },
        {
          to: '/withdrawals',
          label: 'Выводы',
          icon: ArrowUpFromLine,
          ...(pendingWithdrawals > 0 ? { badge: pendingWithdrawals } : {}),
        },
        {
          to: '/deposit-reversals',
          label: 'Сторнирования',
          icon: Undo2,
          ...(pendingDepositReversals > 0 ? { badge: pendingDepositReversals } : {}),
        },
        { to: '/futures', label: 'Фьючерсы', icon: Scale },
        { to: '/kyc', label: 'Верификация', icon: BadgeCheck, ...(pendingKyc > 0 ? { badge: pendingKyc } : {}) },
      ],
    },
    {
      title: 'Платформа',
      items: [
        { to: '/users', label: 'Пользователи', icon: Users },
        { to: '/masters', label: 'Мастера и стратегии', icon: Coins },
        { to: '/signals', label: 'Сигналы', icon: RadioTower },
        { to: '/referrals', label: 'Партнёры', icon: Share2 },
        { to: '/invites', label: 'Приглашения', icon: UserPlus },
        { to: '/networks', label: 'Сети и активы', icon: Network },
        { to: '/announcements', label: 'Объявления', icon: Megaphone },
        { to: '/reports', label: 'Отчёты', icon: FileBarChart },
      ],
    },
    {
      title: 'Система',
      items: [
        { to: '/settings', label: 'Настройки', icon: Settings },
        { to: '/audit', label: 'Аудит', icon: ScrollText },
        { to: '/security', label: 'Безопасность', icon: ShieldCheck, ...(totpEnabled ? {} : { badge: 1, tone: 'down' as const }) },
      ],
    },
  ]
}

/** The sidebar group a screen belongs to. */
function sectionOf(path: string): string | undefined {
  return allGroups(0, 0, true, 0).find((group) => group.items.some((item) => item.to === path))?.title
}

function initialsOf(email: string | undefined): string {
  if (!email) return '·'
  const local = email.split('@')[0] ?? email
  const parts = local.split(/[._\-+]+/).filter(Boolean)
  const letters = parts.length >= 2 ? `${parts[0]![0]}${parts[1]![0]}` : local.slice(0, 2)
  return letters.toUpperCase()
}

/* ---- the rail: remembered choice, collapsed on its own below 1200 px ------- */

const COLLAPSED_KEY = 'admin.sidebar.collapsed'
const NARROW_QUERY = '(max-width: 1199.98px)'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
  } catch {
    // The choice simply is not remembered.
  }
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const media = window.matchMedia(query)
    const update = (): void => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])
  return matches
}

export interface ShellProps {
  /** The signed-in operator's desk; hides what it cannot open. */
  role?: string
  actions?: ReactNode
  pendingWithdrawals?: number
  pendingKyc?: number
  /** Deposit reversals waiting on a decision (the `ledger` desk). */
  pendingDepositReversals?: number
  /** `false` marks Security in the nav: every money decision needs it. */
  totpEnabled?: boolean
  /** The `copy.enabled` setting; off hides «Мастера и стратегии» from the menu. */
  copyEnabled?: boolean
  operator?: string
}

export function Shell({
  actions,
  role,
  pendingWithdrawals = 0,
  pendingKyc = 0,
  pendingDepositReversals = 0,
  totpEnabled = true,
  copyEnabled = false,
  operator,
}: ShellProps): JSX.Element {
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const groups = adminNav(pendingWithdrawals, pendingKyc, totpEnabled, role, pendingDepositReversals, copyEnabled)
  const groupsKey = groups.map((group) => group.items.map((item) => item.to).join(',')).join('|')
  const destinations = useMemo<SearchDestination[]>(
    () => groups.flatMap((group) => group.items.map(({ to, label, icon }) => ({ to, label, icon, group: group.title }))),
    // The destinations change with the desk and the menu, not with the counters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupsKey],
  )

  /* rail */
  const narrow = useMediaQuery(NARROW_QUERY)
  const [preferCollapsed, setPreferCollapsed] = useState(readCollapsed)
  const [narrowExpanded, setNarrowExpanded] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  useEffect(() => setNarrowExpanded(false), [narrow])
  // On a narrow window the opened sidebar floats over the page: it folds back
  // once the operator has picked a screen, or on Escape.
  useEffect(() => setNarrowExpanded(false), [pathname])
  useEffect(() => {
    if (!narrow || !narrowExpanded) return
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape' && !document.querySelector('dialog[open]')) setNarrowExpanded(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [narrow, narrowExpanded])
  const collapsed = narrow ? !narrowExpanded : preferCollapsed
  const toggle = (): void => {
    if (narrow) {
      setNarrowExpanded((value) => !value)
      return
    }
    setPreferCollapsed((value) => {
      writeCollapsed(!value)
      return !value
    })
  }

  /* the sliding active indicator: slides on a route change, jumps on a resize */
  const navRef = useRef<HTMLElement>(null)
  const [indicator, setIndicator] = useState({ top: 0, height: 0, visible: false, animate: false })
  const placeIndicator = useCallback((slide: boolean): HTMLElement | null => {
    const link = navRef.current?.querySelector<HTMLElement>('a[aria-current="page"]') ?? null
    if (!link) {
      setIndicator((current) => ({ ...current, visible: false, animate: false }))
      return null
    }
    const top = link.offsetTop
    const height = link.offsetHeight
    setIndicator((current) =>
      current.visible && current.top === top && current.height === height ? current : { top, height, visible: true, animate: slide && current.visible },
    )
    return link
  }, [])
  useLayoutEffect(() => {
    const nav = navRef.current
    const link = placeIndicator(true)
    if (!nav || !link) return
    const navBox = nav.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    if (linkBox.top < navBox.top || linkBox.bottom > navBox.bottom) link.scrollIntoView({ block: 'nearest' })
  }, [pathname, groupsKey, expandedGroups, placeIndicator])
  // A window crossing the short-height breakpoint changes the item height.
  useEffect(() => {
    const onResize = (): void => void placeIndicator(false)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [placeIndicator])

  /* rail tooltips */
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null)
  useEffect(() => setTip(null), [collapsed, pathname])
  const showTip = (event: { currentTarget: HTMLElement }, label: string): void => {
    if (!collapsed) return
    const box = event.currentTarget.getBoundingClientRect()
    setTip({ label, top: box.top + box.height / 2 })
  }
  const hideTip = (): void => setTip(null)

  /* sign-out */
  const [signingOut, setSigningOut] = useState(false)
  const leave = async (): Promise<void> => {
    setSigningOut(true)
    try {
      await signOut()
    } finally {
      queryClient.clear()
    }
  }

  const section = sectionOf(pathname)
  const roleLabel = ROLE_LABEL[role ?? ''] ?? 'оператор'

  return (
    <div className={styles.shell} data-collapsed={collapsed ? 'true' : 'false'} data-narrow={narrow ? 'true' : 'false'}>
      <aside className={styles.sidebar} aria-label="Боковая панель">
        <div className={styles.brand}>
          <BrandMark size={7} />
          <span className={styles.brandName}>19CRT</span>
          <span className={styles.brandChip}>Админ</span>
        </div>

        <nav ref={navRef} className={styles.nav} aria-label="Разделы">
          <span
            className={styles.indicator}
            data-visible={indicator.visible}
            data-animate={indicator.animate}
            style={{ transform: `translateY(${indicator.top}px)`, height: indicator.height || undefined }}
            aria-hidden="true"
          />
          {groups.map((group) => (
            <div key={group.title} className={styles.group}>
              <button
                type="button"
                className={styles.groupTitle}
                aria-label={`${group.title}, ${group.items.length} разделов`}
                aria-expanded={group.title === section || expandedGroups.has(group.title)}
                title={collapsed ? group.title : undefined}
                onClick={() => setExpandedGroups((current) => {
                  return current.has(group.title) ? new Set() : new Set([group.title])
                })}
              >
                <span>{group.title}</span>
                <ChevronDown size={13} aria-hidden="true" />
              </button>
              {group.title === section || expandedGroups.has(group.title) ? group.items.map(({ to, label, icon: Icon, badge, tone }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={styles.item}
                  aria-label={collapsed ? (badge === undefined ? label : `${label}: ${badge}`) : undefined}
                  onMouseEnter={(event) => showTip(event, badge === undefined ? label : `${label} · ${badge}`)}
                  onMouseLeave={hideTip}
                  onFocus={(event) => showTip(event, badge === undefined ? label : `${label} · ${badge}`)}
                  onBlur={hideTip}
                >
                  <span className={styles.itemIcon}>
                    <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                    {badge === undefined ? null : <span className={styles.itemDot} data-tone={tone ?? 'warning'} aria-hidden="true" />}
                  </span>
                  <span className={styles.itemLabel}>{label}</span>
                  {badge === undefined ? null : (
                    <span className={styles.count} data-tone={tone ?? 'warning'}>
                      {badge}
                    </span>
                  )}
                </NavLink>
              )) : null}
            </div>
          ))}
        </nav>

        <div className={styles.footer}>
          <span className={styles.avatar} aria-hidden="true" title={operator}>
            {initialsOf(operator)}
          </span>
          <span className={styles.who}>
            <span className={styles.whoEmail} title={operator}>
              {operator ?? '—'}
            </span>
            <span className={styles.whoRole}>{roleLabel}</span>
          </span>
          <button
            type="button"
            className={styles.signOut}
            onClick={() => void leave()}
            disabled={signingOut}
            aria-label="Выйти из панели"
            title="Выйти"
            onMouseEnter={(event) => showTip(event, 'Выйти')}
            onMouseLeave={hideTip}
          >
            <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>
      </aside>

      {narrow && !collapsed ? <div className={styles.scrim} onClick={() => setNarrowExpanded(false)} aria-hidden="true" /> : null}

      <div className={styles.main}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={toggle}
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {collapsed ? <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden="true" /> : <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden="true" />}
          </button>
          <div className={styles.actions}>
            {canOpen(role, '/') ? <GlobalSearch destinations={destinations} /> : null}
            {actions}
            <HealthIndicator canOpenOverview={canOpen(role, '/')} />
            <PlatformClock />
          </div>
        </header>
        <main className={styles.content}>
          <div key={pathname} className={styles.page}>
            <Outlet />
          </div>
        </main>
      </div>

      {tip && collapsed
        ? createPortal(
            <div className={styles.tip} style={{ top: tip.top }} role="tooltip">
              {tip.label}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
